import UrgentRequestForm, { FormType, UrgentRequestFormData } from '../../components/orders/urgent-request-form';
import OrderSearchForm from '../../components/orders/order-search-form';
import DeliveryDateHistoryModal from '../../components/orders/delivery-date-history-modal';
import MessageHelper from '../../helpers/message-helper';
import AbstractAsyncTablePage from '../abstract-async-table-page';
import Table from '../../components/shared/table';
import HTMLTemplateHelper from '../../helpers/html-template-helper';
import SalesOrderData from '../../model/sales-order-data';
import SalesOrderEntryData from '../../model/sales-order-entry-data';
import { formatDate } from '../../helpers/utils';

interface SalesOrderEntryDataExtended extends SalesOrderEntryData {
  orderItemId: string;
  itemId: number;
  orderId: string;
  externalId: string;
  voucherDate: number;
}

export default class BackOrderPage extends AbstractAsyncTablePage {
  protected readonly searchForm: OrderSearchForm;
  protected readonly table: Table;
  protected readonly tableColumnDefinitions: DataTables.ColumnDefsSettings[] = [{
    targets: 0,
    data: 'orderId',
  }, {
    targets: 1,
    data: 'externalId',
  }, {
    targets: 2,
    data: (data: SalesOrderEntryDataExtended, type: string): string | number => {
      if (data.voucherDate === null) {
        return 0;
      }
      if (type === 'sort') {
        return data.voucherDate;
      }
      return formatDate(new Date(data.voucherDate));
    },
  }, {
    targets: 3,
    data: 'materialNumber',
  }, {
    targets: 4,
    data: 'description',
  }, {
    targets: 5,
    data: 'orderQuantity',
  }, {
    targets: 6,
    data: 'deliveredQuantity',
  }, {
    targets: 7,
    data: 'reference',
  }, {
    targets: 8,
    data: (data: SalesOrderEntryDataExtended, type: string): string | number | null => {
      if (type === 'sort') {
        return data.estimatedDeliveryDate;
      }
      return data.estimatedDeliveryDateFormatted;
    },
  }, {
    targets: 9,
    className: 'text-align-center',
    data: (data: SalesOrderEntryDataExtended): string | null => {
      if (!data.urgencyRequest) {
        return null;
      }

      let icon = 'envelope';
      let color: string;
      switch (data.urgencyRequest.urgencyRequest) {
        case 'REQUEST_ACCESSORIES':
          color = 'yellow';
          break;
        case 'REQUEST_ORIGINAL_PARTS':
          color = 'red';
          break;
        case 'READ_REQUEST_PARTS':
        case 'READ_REQUEST_ACCESSORIES':
          icon = 'envelope-open-text';
          color = 'green';
          break;
        default:
          color = '';
      }
      if (!color.length) {
        return null;
      }

      return HTMLTemplateHelper.createButton({
        text: MessageHelper.getMessage('common.open'),
        textClass: 'sr-only',
        icon: `fas fa-${icon}`,
        buttonClass: `-clear -compact js-openUrgencyRequest color-${color}`,
        dataAttributes: { id: data.orderItemId.toString() },
      }).outerHTML;
    },
  }];

  private rows: SalesOrderEntryDataExtended[] = [];

  constructor(private body: JQuery) {
    super();
    this.searchForm = new OrderSearchForm(body.find('.js-backorderForm') as JQuery<HTMLFormElement>);
    this.table = new Table(body.find('.js-table'), this.tableColumnDefinitions);

    this.bindOpenUrgencyRequestModal();
    this.bindOpenDeliveryDateHistoryModal();
    this.bindAsyncData();
  }

  private bindOpenUrgencyRequestModal(): void {
    this.body.on('click', '.js-openUrgencyRequest', (e) => {
      const button = e.target as HTMLButtonElement;
      const rowData = this.rows.find((row) => row.orderItemId === button.dataset.id);

      if (!rowData) return;

      const deliveryTicketId = rowData.deliveryTicketData?.id;
      if (deliveryTicketId) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        $.get(`${window.ACC.config.encodedContextPath}/orders/backorder/urgencyRequest/${deliveryTicketId}`)
          .fail((error) => window.NotificationHelper.showError(error.responseJSON))
          .done((response: UrgentRequestFormData) => {
            BackOrderPage.openUrgentRequestModal(rowData, response);
          });
      } else {
        BackOrderPage.openUrgentRequestModal(rowData);
      }
    });
  }

  private bindOpenDeliveryDateHistoryModal(): void {
    this.body.on('click', '.js-openDeliveryDateHistory', (e) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const dates = this.rows.find((row) => row.id === Number(e.target.dataset.id))?.deliveryDateChangeHistory;
        if (dates) {
          const deliveryDateModal = new DeliveryDateHistoryModal(dates);
          deliveryDateModal.open();
        }
      } catch (error) {
        window.NotificationHelper.showError(MessageHelper.getMessage('eparts.orderfeatures.backorder.deliveryhistory.error'));
      }
    });
  }

  static openUrgentRequestModal(rowData: SalesOrderEntryDataExtended, requestFormData?: UrgentRequestFormData): void {
    const {
      itemId,
      orderId,
      materialNumber,
      description,
      orderQuantity,
      equipment,
    } = rowData;

    const formData = {
      itemId,
      orderId,
      materialNumber,
      description,
      equipment,
      quantity: orderQuantity,
      salesTicketId: rowData.deliveryTicketData?.id,
      formType: rowData.urgencyRequest?.urgencyRequest as FormType,
    };

    if (requestFormData) {
      Object.assign(formData, requestFormData);
      formData.materialNumber = rowData.materialNumber;
    }

    const urgentRequestForm = new UrgentRequestForm(formData);
    urgentRequestForm.open();
  }

  protected setRows(rows: SalesOrderData[]): void {
    const mappedData: SalesOrderEntryDataExtended[] = rows.flatMap(
      (data: SalesOrderData) => data.entries.map((entry: SalesOrderEntryData) => ({
        orderItemId: `${data.orderId}_${entry.id}`,
        itemId: entry.id,
        orderId: data.orderId,
        externalId: data.externalId,
        voucherDate: data.voucherDate,
        ...entry,
      })),
    );
    this.rows = mappedData;
    this.table.setRows(mappedData);
  }
}
